// src/providers/preferencesProvider.tsx
//
// User preferences store (Decision 8 / proposal §10, mockup 13 "Mis
// preferencias"). Loads `GET /api/v1/ui/users/me/preferences` once on
// mount, merges the result over sane defaults, and writes changes back with
// a debounced `PUT`. When the endpoint is unavailable (404, network error —
// the API lane may not have shipped it yet) every read and write falls back
// to localStorage transparently, so callers never need to know which path
// is active.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { uiApiUrl } from '@/utils/uiApiUrl';
import { isDocumentationOnly } from '@/features/developers/access';
import keycloak from '@/lib/keycloak';

export type TableDensity = 'comfortable' | 'compact';
export type LandingPage = 'arrivals' | 'reservations' | 'summary';

/** Multi-hotel scope selection (Decision 6), remembered per user. */
export interface ScopePreference {
  /** `null` = all hotels of the current group/scope. Otherwise the explicit hotel id list. */
  hotelIds: string[] | null;
  /** «Solo con llegadas» — client-side filter toggle, wired to a prop, no-op until wave 2/3 read it. */
  onlyWithArrivals: boolean;
}

export interface UserPreferences {
  language: 'es' | 'en';
  landingPage: LandingPage;
  scope: ScopePreference;
  tableDensity: TableDensity;
  /**
   * Table-header view switches (mockup 05 callout 8: counts vs %, table vs
   * chart…), keyed by page then by switch name. Free-form so each analytics
   * page (Resumen, Canales, Tiempos, Cobros, Hoteles) can persist its own
   * switches without a schema change here.
   */
  pageViews: Record<string, Record<string, string>>;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  language: 'es',
  landingPage: 'summary',
  scope: { hotelIds: null, onlyWithArrivals: false },
  tableDensity: 'comfortable',
  pageViews: {},
};

const STORAGE_KEY = 'insight.preferences';
const WRITE_DEBOUNCE_MS = 800;

const readLocalStorage = (): Partial<UserPreferences> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<UserPreferences>) : {};
  } catch {
    return {};
  }
};

const writeLocalStorage = (prefs: UserPreferences): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage full/unavailable — nothing more we can do, the in-memory state still works.
  }
};

// BASE_URL already ends in the `ui` segment, like every other call in the data provider.
const preferencesUrl = () => uiApiUrl('users/me/preferences');

interface PreferencesContextValue {
  preferences: UserPreferences;
  loading: boolean;
  /** Source that actually served the last successful read/write. */
  source: 'server' | 'local';
  setPreferences: (patch: Partial<UserPreferences>) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferencesState] = useState<UserPreferences>({
    ...DEFAULT_PREFERENCES,
    ...readLocalStorage(),
  });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<'server' | 'local'>('local');
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // A documentation-only person has no preferences to read: the API would refuse the request.
      if (isDocumentationOnly()) {
        if (!cancelled) setLoading(false);
        return;
      }
      // The effect runs before Keycloak has finished restoring the session, so a
      // missing token here means "not ready yet", never "the server refused".
      let token = keycloak.token;
      for (let attempt = 0; !token && attempt < 20 && !cancelled; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        token = keycloak.token;
      }
      if (!token || cancelled) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const response = await fetch(preferencesUrl(), {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = await response.json();
        const data = (body?.data ?? body) as Partial<UserPreferences>;
        if (!cancelled && data && typeof data === 'object') {
          const merged = { ...DEFAULT_PREFERENCES, ...readLocalStorage(), ...data };
          setPreferencesState(merged);
          writeLocalStorage(merged);
          setSource('server');
        }
      } catch {
        // Endpoint not ready yet or offline — keep the localStorage-seeded state.
        if (!cancelled) setSource('local');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const setPreferences = useCallback((patch: Partial<UserPreferences>) => {
    setPreferencesState((prev) => {
      const next = { ...prev, ...patch };
      writeLocalStorage(next);

      if (writeTimer.current) clearTimeout(writeTimer.current);
      writeTimer.current = setTimeout(() => {
        const token = keycloak.token;
        if (!token) return;
        fetch(preferencesUrl(), {
          method: 'PUT',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(next),
        })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            setSource('server');
          })
          .catch(() => {
            // Saved locally already — the server write can retry next change.
            setSource('local');
          });
      }, WRITE_DEBOUNCE_MS);

      return next;
    });
  }, []);

  const value = useMemo<PreferencesContextValue>(
    () => ({ preferences, loading, source, setPreferences }),
    [preferences, loading, source, setPreferences]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = (): PreferencesContextValue => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return ctx;
};

/**
 * A single persisted table-header view switch (mockup callout 8), e.g.
 * `useViewSwitch('summary', 'rowsView', 'count')` for the counts-vs-% toggle
 * on Resumen's rows table. Falls back to `defaultValue` until the store has
 * loaded or the user never touched the switch.
 */
export const useViewSwitch = (
  page: string,
  key: string,
  defaultValue: string
): [string, (value: string) => void] => {
  const { preferences, setPreferences } = usePreferences();
  const value = preferences.pageViews[page]?.[key] ?? defaultValue;
  const setValue = useCallback(
    (next: string) => {
      setPreferences({
        pageViews: {
          ...preferences.pageViews,
          [page]: { ...preferences.pageViews[page], [key]: next },
        },
      });
    },
    [page, key, preferences.pageViews, setPreferences]
  );
  return [value, setValue];
};
